import React, { useCallback, useEffect, useState } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { GraphQLTestPage } from "./components/GraphQLTestPage";
import LoginPage from "./components/LoginPage";
import SearchPage from "./components/search";
// ✅ KEEP: Filter caching (but remove UI components)
import { FilterProvider } from "./context/FiltersContext";
import DomainSearchApp from "./DomainSearchApp";
import apiService from "./services/apiService";
import llmApiService from "./services/LLMapiService";
import { ENVIRONMENTS, ENV_STORAGE_KEY } from "./config/environments";
import { switchEnvironment } from "./services/environmentSwitcher";

// Make apiService globally available
if (typeof window !== "undefined") {
	window.apiService = apiService;
	window.llmApiService = llmApiService;
}

// Main App Component with Authentication Logic
const AppContent = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState(null);

	const navigate = useNavigate();
	const location = useLocation();

	// ✅ ADD: Authentication failure handler
	const handleAuthFailure = useCallback(
		(error) => {
			console.log("🔐 Authentication failure detected:", error?.message);

			if (handleAuthFailure.isHandling) return;
			handleAuthFailure.isHandling = true;

			// Clear ApiService authentication
			if (apiService.signOut) {
				apiService.signOut();
			}

			// Clear local state and storage
			localStorage.removeItem("isAuthenticated");
			localStorage.removeItem("userData");
			localStorage.removeItem("google_id_token");
			localStorage.removeItem("google_refresh_token");

			setIsAuthenticated(false);
			setUser(null);

			// Only redirect if not already on login page
			if (location.pathname !== "/auth/login") {
				console.log("🔄 Redirecting to login page...");
				navigate("/auth/login", { replace: true });
			}

			setTimeout(() => {
				handleAuthFailure.isHandling = false;
			}, 1000);
		},
		[navigate, location.pathname],
	);

	// ✅ ADD: Register auth failure callback on mount
	useEffect(() => {
		console.log("🔗 Registering authentication failure callback...");
		// Check if apiService has the method
		if (apiService && typeof apiService.onAuthFailure === "function") {
			apiService.onAuthFailure(handleAuthFailure);
			console.log("✅ Auth failure callback registered successfully");
		} else {
			console.error("❌ apiService.onAuthFailure method not found");
		}

		// Cleanup on unmount
		return () => {
			if (apiService && typeof apiService.onAuthFailure === "function") {
				apiService.onAuthFailure(null);
				console.log("🧹 Auth failure callback cleanup completed");
			}
		};
	}, [handleAuthFailure]);

	// ✅ EXISTING: Keep all your existing useEffect and handler code...
	useEffect(() => {
		// No-op: Google auth removed
	}, []);

	const handleLoginSuccess = useCallback(
		(userData = null) => {
			const finalUserData = userData || apiService.getUser();
			setUser(finalUserData);
			setIsAuthenticated(true);

			// Store authentication state
			localStorage.setItem("isAuthenticated", "true");
			if (finalUserData) {
				localStorage.setItem("userData", JSON.stringify(finalUserData));
			}

			// Redirect to dashboard (root URL)
			navigate("/", { replace: true });
		},
		[navigate],
	);

	const handleLogout = useCallback(() => {
		// Clear ApiService authentication
		if (apiService.signOut) {
			apiService.signOut();
		}

		// Clear local state and storage
		localStorage.removeItem("isAuthenticated");
		localStorage.removeItem("userData");
		localStorage.removeItem("google_id_token");

		setIsAuthenticated(false);
		setUser(null);

		// Redirect to login
		navigate("/auth/login", { replace: true });
	}, [navigate]);

	// ✅ REST OF YOUR EXISTING CODE remains the same...
	const checkExistingAuth = useCallback(() => {
		try {
			// Check ApiService first
			const apiServiceAuth = apiService.isAuthenticated();
			const apiServiceUser = apiService.getUser();

			if (apiServiceAuth && apiServiceUser) {
				setUser(apiServiceUser);
				setIsAuthenticated(true);

				localStorage.setItem("isAuthenticated", "true");
				localStorage.setItem("userData", JSON.stringify(apiServiceUser));

				// If on login page and authenticated, redirect to dashboard
				if (location.pathname === "/auth/login") {
					navigate("/", { replace: true });
				}

				setIsLoading(false);
				return;
			}

			// Fallback: check localStorage
			const isAuth = localStorage.getItem("isAuthenticated");
			const userData = localStorage.getItem("userData");

			if (isAuth === "true") {
				setUser(userData ? JSON.parse(userData) : null);
				setIsAuthenticated(true);

				// If on login page and authenticated, redirect to dashboard
				if (location.pathname === "/auth/login") {
					navigate("/", { replace: true });
				}
			}
		} catch (error) {
			console.error("❌ Auth check error:", error);
		} finally {
			setIsLoading(false);
		}
	}, [navigate, location.pathname]);

	useEffect(() => {
		checkExistingAuth();
	}, [checkExistingAuth]);

	// Monitor auth changes
	useEffect(() => {
		const interval = setInterval(() => {
			const apiServiceAuth = apiService.isAuthenticated();
			if (apiServiceAuth && !isAuthenticated) {
				const apiServiceUser = apiService.getUser();
				handleLoginSuccess(apiServiceUser);
			}
		}, 5000);

		return () => clearInterval(interval);
	}, [isAuthenticated, handleLoginSuccess]);

	// ✅ REST OF YOUR COMPONENT CODE remains exactly the same...
	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
					color: "white",
				}}
			>
				<div style={{ textAlign: "center" }}>
					<div
						style={{
							width: "60px",
							height: "60px",
							border: "6px solid rgba(255, 255, 255, 0.3)",
							borderTop: "6px solid white",
							borderRadius: "50%",
							animation: "spin 1s linear infinite",
							margin: "0 auto 20px",
						}}
					/>
					<h2>Loading...</h2>
					<p>Checking authentication status</p>
				</div>
				<style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
			</div>
		);
	}

	return (
		<Routes>
			{/* Login Route */}
			<Route
				path="/auth/login"
				element={
					isAuthenticated ? (
						<Navigate to="/" replace />
					) : (
						<LoginPage onLoginSuccess={handleLoginSuccess} />
					)
				}
			/>

			{/* Dashboard Route (Root) */}
			<Route
				path="/"
				element={
					isAuthenticated ? (
						<DashboardWithHeader user={user} onLogout={handleLogout} />
					) : (
						<Navigate to="/auth/login" replace />
					)
				}
			/>

			<Route
				path="/search"
				element={
					isAuthenticated ? (
						<SearchPage user={user} onLogout={handleLogout} />
					) : (
						<Navigate to="/auth/login" replace />
					)
				}
			/>

			<Route
				path="/graphql-test"
				element={
					isAuthenticated ? (
						<GraphQLTestPage />
					) : (
						<Navigate to="/auth/login" replace />
					)
				}
			/>

			{/* Catch all route - redirect to dashboard */}
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
};

// ✅ UPDATED: Dashboard component with fixed app section navigation
const DashboardWithHeader = ({ user, onLogout }) => {
	const [showUserMenu, setShowUserMenu] = useState(false);
	const navigate = useNavigate();

	const savedProxy = (() => { try { return localStorage.getItem(ENV_STORAGE_KEY); } catch (_) { return null; } })();
	const [activeEnv, setActiveEnv] = useState(
		ENVIRONMENTS.find((e) => e.proxy === savedProxy)?.key ?? "dev-int"
	);
	const handleEnvChange = (env) => {
		setActiveEnv(env.key);
		switchEnvironment(env.proxy);
	};

	const getUserInitials = (user) => {
		if (!user) return "U";
		if (user.name) {
			return user.name
				.split(" ")
				.map((n) => n[0])
				.join("")
				.toUpperCase();
		}
		if (user.email) {
			return user.email[0].toUpperCase();
		}
		return "U";
	};

	const handleAISearchClick = () => {
		navigate("/search");
	};

	const handleGraphQLTestClick = () => {
		navigate("/graphql-test");
	};

	return (
		<div className="authenticated-app">
			{/* ✅ MERGED: Header with exact Header.jsx design + user functionality */}
			<header
				style={{
					background: "linear-gradient(135deg, #0076bd 0%, #1fbab0 100%)",
					color: "white",
					padding: "1rem 2rem",
					boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				{/* Left side - Discover Inventory title and subtitle (exact Header.jsx content) */}
				<div>
					<h1 className="header-title">Discover Inventory</h1>
					<h3
						style={{
							margin: "0.25rem 0 0 0",
							fontSize: "1rem",
							fontWeight: "normal",
							opacity: 0.9,
						}}
					>
						Find and select relevant inventory using natural language search
					</h3>
				</div>

				{/* Right side - User menu (preserved functionality) */}
				<div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
					{/* Environment selector */}
					<div style={{ display: "flex", alignItems: "center", background: "rgba(0,0,0,0.2)", borderRadius: "8px", padding: "3px", gap: "2px" }}>
						{ENVIRONMENTS.map((env) => {
							const isActive = activeEnv === env.key;
							return (
								<button
									key={env.key}
									onClick={() => handleEnvChange(env)}
									style={{
										padding: "3px 12px",
										borderRadius: "6px",
										border: "none",
										cursor: "pointer",
										fontSize: "0.78rem",
										fontWeight: isActive ? 700 : 400,
										background: isActive ? "rgba(255,255,255,0.95)" : "transparent",
										color: isActive ? "#0076bd" : "rgba(255,255,255,0.75)",
										transition: "all 0.15s",
									}}
								>
									{env.label}
								</button>
							);
						})}
					</div>

					{/* GraphQL Test Button */}
					<button
						onClick={handleGraphQLTestClick}
						style={{
							background: "rgba(255,255,0,0.15)",
							border: "1px solid rgba(255,255,0,0.4)",
							borderRadius: "8px",
							padding: "0.5rem 1rem",
							color: "white",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							fontSize: "0.9rem",
							fontWeight: "500",
							transition: "all 0.2s ease",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.background = "rgba(255,255,0,0.25)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.background = "rgba(255,255,0,0.15)";
						}}
					>
						Test GraphQL API
					</button>

					{/* AI Search Button */}
					<button
						onClick={handleAISearchClick}
						style={{
							background: "rgba(255,255,255,0.2)",
							border: "1px solid rgba(255,255,255,0.3)",
							borderRadius: "8px",
							padding: "0.5rem 1rem",
							color: "white",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							fontSize: "0.9rem",
							fontWeight: "500",
							transition: "all 0.2s ease",
							backdropFilter: "blur(10px)",
						}}
						onMouseEnter={(e) => {
							e.target.style.background = "rgba(255,255,255,0.3)";
							e.target.style.transform = "translateY(-1px)";
						}}
						onMouseLeave={(e) => {
							e.target.style.background = "rgba(255,255,255,0.2)";
							e.target.style.transform = "translateY(0)";
						}}
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<circle cx="11" cy="11" r="8" />
							<path d="m21 21-4.35-4.35" />
						</svg>
						AI Search
					</button>

					{/* User Menu */}
					<div style={{ position: "relative" }}>
						<button
							onClick={() => setShowUserMenu(!showUserMenu)}
							style={{
								background: "rgba(255,255,255,0.2)",
								border: "none",
								borderRadius: "50%",
								width: "40px",
								height: "40px",
								color: "white",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: "1rem",
								fontWeight: "bold",
								transition: "all 0.2s ease",
							}}
							onMouseEnter={(e) => {
								e.target.style.background = "rgba(255,255,255,0.3)";
							}}
							onMouseLeave={(e) => {
								e.target.style.background = "rgba(255,255,255,0.2)";
							}}
						>
							{getUserInitials(user)}
						</button>

						{showUserMenu && (
							<div
								style={{
									position: "absolute",
									top: "50px",
									right: "0",
									background: "white",
									color: "black",
									borderRadius: "8px",
									boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
									minWidth: "200px",
									zIndex: 1000,
								}}
							>
								<div
									style={{ padding: "1rem", borderBottom: "1px solid #eee" }}
								>
									<div style={{ fontWeight: "bold" }}>
										{user?.name || "User"}
									</div>
									<div style={{ fontSize: "0.9rem", color: "#666" }}>
										{user?.email}
									</div>
								</div>
								<button
									onClick={() => {
										setShowUserMenu(false);
										onLogout();
									}}
									style={{
										width: "100%",
										padding: "1rem",
										border: "none",
										background: "none",
										cursor: "pointer",
										textAlign: "left",
										transition: "background-color 0.2s ease",
									}}
									onMouseEnter={(e) => {
										e.target.style.backgroundColor = "#f5f5f5";
									}}
									onMouseLeave={(e) => {
										e.target.style.backgroundColor = "transparent";
									}}
								>
									Sign Out
								</button>
							</div>
						)}
					</div>
				</div>
			</header>

			{/* Main Domain Search App */}
			<DomainSearchApp user={user} />
		</div>
	);
};

// ✅ KEEP: FilterProvider wrapper (caching still works in background)
const App = () => {
	return (
		<FilterProvider>
			<Router>
				<AppContent />
			</Router>
		</FilterProvider>
	);
};

export default App;
